// LogCatchModal — full catch logging form
// Species, weight, length, notes, photo, GPS position, save to local DB

import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { X, Camera, Fish, Save } from 'lucide-react';
import type { Catch, Photo, Trip } from '@/lib/world-model';
import { saveCatch, savePhotoMeta, saveTrip, getTrip } from '@/lib/local-db';
import { capturePhoto } from '@/lib/camera';

const COMMON_SPECIES = [
  'Brook Trout', 'Brown Trout', 'Rainbow Trout', 'Landlocked Salmon',
  'Smallmouth Bass', 'Largemouth Bass', 'Chain Pickerel', 'Yellow Perch',
  'Lake Trout', 'Striped Bass', 'Bluefish', 'Other',
];

interface LogCatchModalProps {
  tripId: string;
  lat: number;
  lng: number;
  onSave: (c: Catch) => void;
  onClose: () => void;
}

export function LogCatchModal({ tripId, lat, lng, onSave, onClose }: LogCatchModalProps) {
  const [species, setSpecies] = useState('');
  const [customSpecies, setCustomSpecies] = useState('');
  const [weightLbs, setWeightLbs] = useState('');
  const [lengthIn, setLengthIn] = useState('');
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [saving, setSaving] = useState(false);

  const handlePhoto = async () => {
    const photo = await capturePhoto(lat, lng);
    if (photo) setPhotos(prev => [...prev, photo]);
  };

  const handleSave = async () => {
    if (!species && !customSpecies) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const catchId = uuidv4();

      // Save photo metadata
      for (const photo of photos) {
        await savePhotoMeta(photo);
      }

      const newCatch: Catch = {
        id: catchId,
        tripId,
        speciesName: species === 'Other' ? customSpecies : species,
        weightLbs: weightLbs ? Number(weightLbs) : undefined,
        lengthIn: lengthIn ? Number(lengthIn) : undefined,
        photoIds: photos.map(p => p.id),
        notes: notes || undefined,
        lat,
        lng,
        caughtAt: now,
        createdAt: now,
        updatedAt: now,
        syncedAt: undefined,
      };

      await saveCatch(newCatch);

      // Update trip's catchIds list
      const trip = await getTrip(tripId);
      if (trip) {
        trip.catchIds = [...trip.catchIds, catchId];
        trip.updatedAt = now;
        await saveTrip(trip);
      }

      onSave(newCatch);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-[#1c1c1e] rounded-t-3xl pb-safe p-6 space-y-5 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Fish className="w-5 h-5 text-[#1a4a2e]" />
            <h2 className="text-white font-semibold text-lg">Log Catch</h2>
          </div>
          <button onClick={onClose} className="text-[#8e8e93]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Species */}
        <div>
          <label className="text-[#8e8e93] text-xs font-semibold uppercase tracking-wide mb-2 block">
            Species
          </label>
          <div className="flex flex-wrap gap-2">
            {COMMON_SPECIES.map(s => (
              <button
                key={s}
                onClick={() => setSpecies(s)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  species === s
                    ? 'bg-[#1a4a2e] text-white'
                    : 'bg-[#2c2c2e] text-[#8e8e93]'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          {species === 'Other' && (
            <input
              className="mt-2 w-full bg-[#2c2c2e] text-white rounded-xl px-4 py-3 text-sm"
              placeholder="Enter species name"
              value={customSpecies}
              onChange={e => setCustomSpecies(e.target.value)}
            />
          )}
        </div>

        {/* Measurements */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[#8e8e93] text-xs font-semibold uppercase tracking-wide mb-1 block">
              Weight (lbs)
            </label>
            <input
              type="number"
              inputMode="decimal"
              placeholder="0.0"
              value={weightLbs}
              onChange={e => setWeightLbs(e.target.value)}
              className="w-full bg-[#2c2c2e] text-white rounded-xl px-4 py-3 text-sm"
            />
          </div>
          <div>
            <label className="text-[#8e8e93] text-xs font-semibold uppercase tracking-wide mb-1 block">
              Length (in)
            </label>
            <input
              type="number"
              inputMode="decimal"
              placeholder="0.0"
              value={lengthIn}
              onChange={e => setLengthIn(e.target.value)}
              className="w-full bg-[#2c2c2e] text-white rounded-xl px-4 py-3 text-sm"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="text-[#8e8e93] text-xs font-semibold uppercase tracking-wide mb-1 block">
            Notes
          </label>
          <textarea
            rows={3}
            placeholder="Fly pattern, water conditions, technique..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="w-full bg-[#2c2c2e] text-white rounded-xl px-4 py-3 text-sm resize-none"
          />
        </div>

        {/* Photos */}
        <div>
          <label className="text-[#8e8e93] text-xs font-semibold uppercase tracking-wide mb-2 block">
            Photos
          </label>
          <div className="flex gap-2 flex-wrap">
            {photos.map(p => (
              <div key={p.id} className="w-20 h-20 rounded-xl overflow-hidden bg-[#2c2c2e]">
                <img src={p.thumbnailUri ?? p.localUri} className="w-full h-full object-cover" />
              </div>
            ))}
            <button
              onClick={handlePhoto}
              className="w-20 h-20 rounded-xl bg-[#2c2c2e] flex flex-col items-center justify-center gap-1 text-[#8e8e93] active:bg-[#3a3a3c] transition"
            >
              <Camera className="w-6 h-6" />
              <span className="text-xs">Add</span>
            </button>
          </div>
        </div>

        {/* Location */}
        <div className="text-[#8e8e93] text-xs">
          📍 {lat.toFixed(5)}, {lng.toFixed(5)}
        </div>

        {/* Save */}
        <button
          onClick={handleSave}
          disabled={saving || (!species && !customSpecies)}
          className="w-full py-4 rounded-2xl bg-[#1a4a2e] text-white font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-40 active:scale-95 transition"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Saving...' : 'Save Catch'}
        </button>
      </div>
    </div>
  );
}
